import React, { useEffect } from 'react'
import { css } from '@emotion/core'
import { Paragraph, Ellipsis } from '@fs/zion-ui'
import { i18n } from '@fs/zion-locale'
import { usePerson, updatePersonObject } from '@fs/zion-person-service'
import { string, number, bool, shape, oneOfType } from 'prop-types'
import { useTranslation } from 'react-i18next'
import { getDisplayName } from '../../helpers/name_formatting'
import colorsSet from '../../pages/Activity/colors'
import { ColorThemeType } from '../../sharedPropTypes/activityProps'
import PersonAvatar from '../PersonAvatar'
import { CompletedCountIndicator } from '../ActivityCard/CompletedCountIndicator'

export default function PersonCard({ colorTheme, count, person, showRoman }) {
  const [t] = useTranslation()
  const personData = usePerson(person?.personId, 'card', null, true)
  const title = getDisplayName(personData, showRoman, true)
  const imgUrl = personData?.portraitUrl ?? person.portraitUrl

  if (!colorTheme) {
    colorTheme = personData?.gender?.toLowerCase().includes('female') ? 'red' : 'blue'
  }

  const color = colorsSet[colorTheme]
  const cardCss = css`
    height: 160px;
    width: 160px;
    border-radius: 10px;
    background-color: ${color?.card?.primary};
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    position: relative;
  `

  useEffect(() => {
    updatePersonObject(person?.personId, 'card') // this will refetch the person data in the proper language if user changes language
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [i18n.language, person?.personId])

  return (
    <>
      <div css={cardCss} data-testid="person-card">
        <PersonAvatar src={imgUrl} person={{ ...person, ...personData }} size="xl" />
        {count > 0 && <CompletedCountIndicator count={count} personId={person?.personId} />}
      </div>
      <Paragraph data-testid="caption" id={`card-label-${person?.personId}`} size="xs" dense>
        <Ellipsis lineClamp={2}>{title}</Ellipsis>
        {personData &&
          personData?.lifespan?.replace(
            /Deceased|Dead$/gim,
            t('card.person.lifespan.deceased', 'Deceased') // translate DECEASED value in lifespan if present (failsafe if person data fetch fails)
          )}
      </Paragraph>
    </>
  )
}

PersonCard.propTypes = {
  colorTheme: ColorThemeType,
  count: oneOfType([string, number]),
  person: shape({
    personId: string,
  }),
  showRoman: bool,
}
